/**
 * ce-load-more — replaces Horizon's numbered pagination with a LOAD MORE
 * button. Custom cultureEDIT file (not part of Horizon 4.1.1).
 *
 * Round 2, R2-02. Turning infinite scroll off fixed the real problem in
 * change 14, the footer being unreachable, but Horizon only has two modes:
 * on gives infinite scroll, off gives numbered pages. /collections/dildos
 * runs 1 2 3 ... 44 and nobody clicks to 44.
 *
 * WHY THIS IS JS AND NOT A LIQUID FORK. The brief said to change
 * snippets/pagination-controls.liquid, but that file and the
 * snippets/product-grid.liquid that renders it are both Horizon core, and
 * core edits get overwritten on every Horizon update. The nav already
 * contains the next URL as a real <a href>, so everything needed is
 * already in the page.
 *
 * It also gets the no-JS requirement for free, and gets it properly. The
 * pagination is hidden BY THIS SCRIPT, not by a stylesheet, so a visitor
 * without JS keeps working numbered pages rather than being left with a
 * button that does nothing. The <a href="?page=2"> links stay in the dom
 * either way, so crawlers still reach every page.
 *
 * TWO THINGS THAT WOULD BREAK IT IF CHANGED CARELESSLY:
 *
 * 1. Appended cards are brought over with document.importNode. Nodes from
 *    a DOMParser document belong to that document, and custom elements do
 *    not upgrade when you move them across as-is, so quick-add and the
 *    image carousel would be dead on every card past page 1. Importing
 *    adopts them into this document and they upgrade on insert.
 *
 * 2. Horizon's <results-list> REPLACES its own contents when a filter or
 *    sort changes, which throws away the button. A MutationObserver puts
 *    it back. Without that, Load more works until the first filter click
 *    and then silently reverts to numbered pages.
 */
(() => {
  if (window.__ceLoadMoreInit) return;
  window.__ceLoadMoreInit = true;

  const SEL = {
    list: 'results-list',
    nav: 'nav.pagination',
    grid: '.product-grid',
    count: '.products-count-wrapper [role="status"], .products-count-wrapper span',
  };

  const t = {
    more: 'Load more',
    loading: 'Loading',
    // "Showing 48 of 1,044" / "Showing all 1,044"
    showing: (n, total) => `Showing ${fmt(n)} of ${fmt(total)}`,
    all: (total) => `Showing all ${fmt(total)}`,
  };

  const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : n);

  /** The "next" link is the last arrow in the nav; absent on the last page. */
  const nextLink = (nav) => {
    const arrows = [...nav.querySelectorAll('a.pagination__link--arrow[href]')];
    const last = arrows[arrows.length - 1];
    // A single arrow could be "previous" on the final page, so only trust
    // one that is not the first item in the list.
    if (!last) return null;
    const item = last.closest('.pagination__item');
    const items = [...nav.querySelectorAll('.pagination__item')];
    return item === items[0] ? null : last.getAttribute('href');
  };

  /** Total results, read from Horizon's own count line. */
  const totalFor = (root) => {
    const el = (root.closest('.shopify-section') || document).querySelector(SEL.count);
    if (!el) return null;
    const m = el.textContent.replace(/,/g, '').match(/(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  };

  const countCards = (grid) => grid.querySelectorAll(':scope > li').length;

  function build(list, nav, grid) {
    const href = nextLink(nav);
    const total = totalFor(list);

    const wrap = document.createElement('div');
    wrap.className = 'ce-loadmore';
    wrap.dataset.ceLoadmore = '';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ce-loadmore__btn';
    btn.textContent = t.more;

    // Polite, not assertive: appending cards is not an interruption, and
    // assertive would talk over the reader mid-sentence on every click.
    const status = document.createElement('p');
    status.className = 'ce-loadmore__status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    const setStatus = () => {
      const shown = countCards(grid);
      if (total == null) {
        status.textContent = '';
        return;
      }
      status.textContent = wrap.dataset.done === '' ? t.all(total) : t.showing(shown, total);
    };

    let next = href;

    const load = async () => {
      if (!next || btn.disabled) return;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      const label = btn.textContent;
      btn.textContent = t.loading;

      try {
        const res = await fetch(next, { headers: { 'X-Requested-With': 'fetch' } });
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

        const incoming = doc.querySelector(SEL.grid);
        if (incoming) {
          const frag = document.createDocumentFragment();
          incoming.querySelectorAll(':scope > li').forEach((li) => {
            // importNode, see the note at the top of this file.
            frag.appendChild(document.importNode(li, true));
          });
          grid.appendChild(frag);
          // Lets app code catch up with the new cards. Tipo draws its bid
          // widgets once at page load, so lots past the first page had no
          // starting bid, timer or Remind Me; ce-tipo-selectors listens
          // for this and re-runs Tipo.
          grid.dispatchEvent(new CustomEvent('ce:loadmore', { bubbles: true }));
        }

        const incomingNav = doc.querySelector(SEL.nav);
        next = incomingNav ? nextLink(incomingNav) : null;

        if (!next) {
          wrap.dataset.done = '';
          btn.remove();
        }
      } catch (err) {
        // Put the button back rather than stranding the visitor: the
        // numbered pagination is still in the dom underneath, so the
        // page remains navigable either way.
        console.warn('[ce-load-more]', err);
        btn.textContent = label;
      } finally {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        if (btn.isConnected) btn.textContent = t.more;
        setStatus();
      }
    };

    btn.addEventListener('click', load);

    if (href) wrap.appendChild(btn);
    else wrap.dataset.done = '';
    wrap.appendChild(status);
    setStatus();

    nav.insertAdjacentElement('afterend', wrap);
    list.classList.add('ce-loadmore-on');
  }

  function enhance(root) {
    root.querySelectorAll(SEL.list).forEach((list) => {
      // Only where Horizon rendered numbered pages, i.e. infinite scroll
      // is off. If it is on, leave it entirely alone.
      const nav = list.querySelector(SEL.nav);
      const grid = list.querySelector(SEL.grid);
      if (!nav || !grid) return;
      if (list.querySelector('[data-ce-loadmore]')) return;
      build(list, nav, grid);
    });
  }

  function watch() {
    document.querySelectorAll(SEL.list).forEach((list) => {
      if (list.__ceWatched) return;
      list.__ceWatched = true;
      // Re-enhance after a filter or sort swaps the contents out.
      const mo = new MutationObserver(() => {
        if (list.querySelector(SEL.nav) && !list.querySelector('[data-ce-loadmore]')) {
          list.classList.remove('ce-loadmore-on');
          enhance(list.parentElement || document);
        }
      });
      mo.observe(list, { childList: true, subtree: true });
    });
  }

  const init = () => {
    enhance(document);
    watch();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
